import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Star, Radio, StickyNote, Send, Lock, Play, MessageSquare, ChevronRight, Bell, ThumbsUp, Share2, BadgeCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const posterUrl = (p) => (p?.startsWith("http") ? p : p ? `${BACKEND_URL}/api/files/${p}` : "");
const fmt = (s) => { s = Math.floor(s || 0); const m = Math.floor(s / 60); return `${m}:${String(s % 60).padStart(2, "0")}`; };

const TIERS = [
  { key: "ppv", label: "Pay-per-view", sub: "Watch once" },
  { key: "rent_day", label: "1 Day", sub: "24h access" },
  { key: "rent_week", label: "1 Week", sub: "7 days" },
  { key: "rent_month", label: "1 Month", sub: "30 days" },
  { key: "rent_year", label: "1 Year", sub: "365 days" },
  { key: "subscription", label: "Subscription", sub: "30 days, renews" },
];

export default function WatchContent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const chatEndRef = useRef(null);
  const viewedRef = useRef(false);
  const [c, setC] = useState(null);
  const [notes, setNotes] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [chat, setChat] = useState([]);
  const [live, setLive] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [reviewText, setReviewText] = useState("");
  const [chatText, setChatText] = useState("");
  const [screen, setScreen] = useState({ name: "", email: "", message: "", preferred_date: "" });

  const load = useCallback(async () => {
    try {
      const [d, n, r] = await Promise.all([api.watchContent(id), api.listNotes(id), api.listReviews(id)]);
      setC(d); setNotes(n.notes || []); setReviews(r.reviews || []);
    } catch { toast.error("Content not found"); navigate("/app/watch"); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  // purchase return
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("purchase") === "success" && p.get("session_id")) {
      (async () => { try { await api.boxOfficeStatus(p.get("session_id")); toast.success("Payment received — enjoy the show!"); await load(); } catch { /* ignore */ } })();
      window.history.replaceState(null, "", `/app/watch/${id}`);
    } else if (p.get("purchase") === "cancel") {
      toast("Payment cancelled."); window.history.replaceState(null, "", `/app/watch/${id}`);
    }
  }, [id, load]);

  // live chat polling
  const loadChat = useCallback(async () => { try { const r = await api.listChat(id); setChat(r.messages || []); } catch { /* ignore */ } }, [id]);
  useEffect(() => {
    if (!live) return;
    loadChat();
    const t = setInterval(loadChat, 5000);
    return () => clearInterval(t);
  }, [live, loadChat]);
  useEffect(() => { if (live) chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chat, live]);

  const onPlay = async () => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    try { await api.bumpView(id); } catch { /* ignore */ }
  };

  const seekTo = (t) => { if (videoRef.current) { videoRef.current.currentTime = t; videoRef.current.play?.(); } };

  const addNote = async () => {
    if (!noteText.trim()) return;
    const t = videoRef.current?.currentTime || 0;
    try { await api.addNote(id, t, noteText.trim()); setNoteText(""); const n = await api.listNotes(id); setNotes(n.notes || []); toast.success("Note pinned"); }
    catch { toast.error("Could not add note"); }
  };

  const wordCount = reviewText.trim() ? reviewText.trim().split(/\s+/).length : 0;
  const addReview = async () => {
    if (!reviewText.trim()) return;
    if (wordCount > 160) return toast.error("Reviews are limited to 160 words");
    try { await api.addReview(id, reviewText.trim()); setReviewText(""); const r = await api.listReviews(id); setReviews(r.reviews || []); toast.success("Review posted"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not post"); }
  };

  const rate = async (stars) => {
    try { const r = await api.rateContent(id, stars); setC(cc => ({ ...cc, my_rating: stars, rating_avg: r.rating_avg, rating_count: r.rating_count })); toast.success(`Rated ${stars}★`); }
    catch { toast.error("Could not rate"); }
  };

  const subscribe = async () => { try { const r = await api.subscribeContent(id); setC(cc => ({ ...cc, my_subscribed: r.subscribed, subs_count: r.subs_count })); toast.success(r.subscribed ? "Subscribed" : "Unsubscribed"); } catch { toast.error("Failed"); } };
  const recommend = async () => { try { const r = await api.recommendContent(id); setC(cc => ({ ...cc, my_recommended: r.recommended, rec_count: r.rec_count })); } catch { toast.error("Failed"); } };
  const refer = async () => { try { const r = await api.referContent(id); setC(cc => ({ ...cc, ref_count: r.ref_count })); await navigator.clipboard.writeText(r.link); toast.success("Referral link copied"); } catch { toast.error("Failed"); } };

  const sendChat = async () => {
    if (!chatText.trim()) return;
    try { await api.postChat(id, chatText.trim()); setChatText(""); await loadChat(); } catch { toast.error("Could not send"); }
  };
  const react = async (emoji) => { try { await api.postChat(id, emoji); await loadChat(); } catch { /* ignore */ } };

  const buy = async (kind) => {
    try {
      const r = await api.purchaseContent(id, kind, window.location.origin);
      if (r.checkout_url) { toast.success("Redirecting to checkout…"); window.location.href = r.checkout_url; }
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start checkout"); }
  };

  const requestScreening = async () => {
    if (!screen.name.trim() || !screen.email.includes("@")) return toast.error("Name and valid email required");
    try { await api.screeningRequest(id, screen); toast.success("Screening request sent to the streamer"); setScreen({ name: "", email: "", message: "", preferred_date: "" }); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
  };

  if (!c) return <div className="min-h-[60vh] flex items-center justify-center mono text-kinetic animate-pulse">LOADING…</div>;

  const duration = videoRef.current?.duration || c.duration || 0;
  const tiers = TIERS.filter(t => c.pricing?.[t.key]?.enabled);
  const paidScreening = c.pricing?.paid_screening?.enabled;

  return (
    <div className="space-y-8" data-testid="watch-content">
      <button data-testid="watch-back" onClick={() => navigate("/app/watch")} className="mono text-white/50 hover:text-kinetic inline-flex items-center gap-2">
        <ArrowLeft className="w-4 h-4" /> BACK TO BROWSE
      </button>

      {/* Player / locked poster */}
      <div className="relative rounded-xl overflow-hidden border border-white/10 bg-black">
        {c.has_access && c.video_path !== null ? (
          <div className="relative">
            <video ref={videoRef} data-testid="watch-video" controls onPlay={onPlay} crossOrigin="use-credentials"
              className="w-full max-h-[70vh] bg-black" poster={posterUrl(c.poster)} src={`${API}/stream/${c.id}`} />
            {live && (
              <div className="absolute right-4 bottom-20 w-80 max-h-[60%] bg-black/70 backdrop-blur-xl border border-white/10 rounded-lg overflow-hidden flex flex-col" data-testid="live-chat-overlay">
                <div className="px-3 py-2 border-b border-white/10 mono text-kinetic flex items-center gap-2"><Radio className="w-3 h-3 animate-pulse" /> LIVE WATCH-PARTY</div>
                <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-32 max-h-56">
                  {chat.length === 0 && <div className="mono text-white/30 text-center py-6">SAY SOMETHING 👋</div>}
                  {chat.map(m => (
                    <div key={m.id} className="text-sm"><span className="mono text-kinetic">{m.name}:</span> <span className="text-white/85">{m.body}</span></div>
                  ))}
                  <div ref={chatEndRef} />
                </div>
                <div className="p-2 border-t border-white/10 flex gap-1">
                  <Input data-testid="live-chat-input" value={chatText} onChange={(e) => setChatText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendChat()} placeholder="Message…" className="bg-black border-white/15 rounded-sm h-8 text-sm" />
                  <Button data-testid="live-chat-send" onClick={sendChat} className="kinetic-btn rounded-sm h-8 px-2"><Send className="w-3.5 h-3.5" /></Button>
                </div>
                <div className="px-2 pb-2 flex gap-1">
                  {["❤️", "🔥", "😂", "👏", "😮"].map(e => (
                    <button key={e} onClick={() => react(e)} className="text-lg hover:scale-125 transition-transform">{e}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="relative aspect-video max-h-[70vh] flex items-center justify-center" data-testid="watch-locked">
            <img src={posterUrl(c.poster)} alt={c.title} className="absolute inset-0 w-full h-full object-cover opacity-40" />
            <div className="relative text-center space-y-3">
              <Lock className="w-10 h-10 text-kinetic mx-auto" />
              <div className="font-sans text-2xl font-extrabold">Unlock at the Box Office</div>
              <div className="mono text-white/50">Purchase below to start watching</div>
            </div>
          </div>
        )}
      </div>

      {/* Sticky notes timeline */}
      {c.has_access && (
        <div data-testid="notes-timeline">
          <div className="flex items-center justify-between mb-2">
            <div className="mono text-white/60 flex items-center gap-2"><StickyNote className="w-3.5 h-3.5 text-kinetic" /> IN-VIDEO NOTES</div>
            <button data-testid="live-toggle" onClick={() => setLive(v => !v)}
              className={`mono px-3 h-8 rounded-sm border inline-flex items-center gap-2 ${live ? "border-kinetic text-kinetic bg-kinetic/10" : "border-white/20 text-white/60 hover:text-white"}`}>
              <Radio className="w-3.5 h-3.5" /> {live ? "LIVE ON" : "GO LIVE"}
            </button>
          </div>
          <div className="relative h-8 bg-white/5 rounded-sm border border-white/10">
            {notes.map(n => (
              <button key={n.id} title={`${fmt(n.timestamp)} · ${n.body}`} onClick={() => seekTo(n.timestamp)}
                data-testid={`note-marker-${n.id}`}
                className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 bg-kinetic rounded-full hover:scale-150 transition-transform"
                style={{ left: `${duration ? Math.min(99, (n.timestamp / duration) * 100) : 0}%` }} />
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            <Input data-testid="note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addNote()}
              placeholder="Drop a note at the current timestamp…" className="bg-black border-white/15 rounded-sm" />
            <Button data-testid="note-add" onClick={addNote} className="kinetic-btn rounded-sm shrink-0">Pin @ {fmt(videoRef.current?.currentTime)}</Button>
          </div>
          {notes.length > 0 && (
            <div className="mt-3 space-y-1.5">
              {notes.map(n => (
                <button key={n.id} onClick={() => seekTo(n.timestamp)} className="block w-full text-left text-sm text-white/70 hover:text-white">
                  <span className="mono text-kinetic mr-2">{fmt(n.timestamp)}</span>{n.name}: {n.body}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Title + rating */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="mono text-kinetic">{(c.genre || "FILM").toUpperCase()} · {c.channel} {c.series_id ? `· S${c.season}E${c.episode}` : ""}</div>
          <h1 className="text-4xl font-extrabold tracking-tight mt-2" data-testid="watch-title">{c.title}</h1>
          {c.logline && <p className="text-white/70 mt-2 max-w-2xl">{c.logline}</p>}
          <div className="mono text-white/40 mt-2">{c.views || 0} VIEWS · {c.rating_avg || 0}★ ({c.rating_count || 0})</div>
        </div>
        <div className="text-right">
          <div className="mono text-white/50 mb-1">RATE THIS</div>
          <div className="flex gap-1" data-testid="rating-stars">
            {[1, 2, 3, 4, 5].map(s => (
              <button key={s} data-testid={`rate-${s}`} onClick={() => rate(s)}>
                <Star className={`w-6 h-6 ${(c.my_rating || 0) >= s ? "fill-kinetic text-kinetic" : "text-white/30 hover:text-white/60"}`} />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Social bar: subscriptions / recommendations / referrals */}
      <div className="grid grid-cols-3 gap-3" data-testid="social-bar">
        <button data-testid="subscribe-btn" onClick={subscribe}
          className={`border rounded-lg p-4 text-center transition ${c.my_subscribed ? "border-kinetic bg-kinetic/10" : "border-white/10 hover:border-white/30"}`}>
          <Bell className={`w-5 h-5 mx-auto ${c.my_subscribed ? "text-kinetic" : "text-white/60"}`} />
          <div className="font-sans text-xl font-extrabold mt-1" data-testid="subs-count">{c.subs_count || 0}</div>
          <div className="mono text-white/40">{c.my_subscribed ? "SUBSCRIBED" : "SUBSCRIBE"}</div>
        </button>
        <button data-testid="recommend-btn" onClick={recommend}
          className={`border rounded-lg p-4 text-center transition ${c.my_recommended ? "border-kinetic bg-kinetic/10" : "border-white/10 hover:border-white/30"}`}>
          <ThumbsUp className={`w-5 h-5 mx-auto ${c.my_recommended ? "text-kinetic" : "text-white/60"}`} />
          <div className="font-sans text-xl font-extrabold mt-1" data-testid="rec-count">{c.rec_count || 0}</div>
          <div className="mono text-white/40">RECOMMEND</div>
        </button>
        <button data-testid="refer-btn" onClick={refer} className="border border-white/10 hover:border-white/30 rounded-lg p-4 text-center transition">
          <Share2 className="w-5 h-5 mx-auto text-white/60" />
          <div className="font-sans text-xl font-extrabold mt-1" data-testid="ref-count">{c.ref_count || 0}</div>
          <div className="mono text-white/40">REFER</div>
        </button>
      </div>

      {c.next && (
        <button onClick={() => navigate(`/app/watch/${c.next.id}`)} data-testid="watch-next"
          className="w-full border border-white/10 bg-[#0E0E0E] hover:border-kinetic/50 rounded-lg p-4 flex items-center justify-between transition-colors">
          <div className="text-left"><div className="mono text-white/40">UP NEXT · S{c.next.season}E{c.next.episode}</div><div className="font-sans font-bold mt-0.5">{c.next.title}</div></div>
          <ChevronRight className="w-5 h-5 text-kinetic" />
        </button>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: description + credits + reviews */}
        <div className="lg:col-span-2 space-y-6">
          {c.description && (
            <div className="border border-white/10 bg-[#0C0C0C] rounded-lg p-6">
              <div className="mono text-white/60 mb-2">SYNOPSIS</div>
              <p className="text-white/80 leading-relaxed whitespace-pre-wrap">{c.description}</p>
            </div>
          )}

          {c.credits?.length > 0 && (
            <div className="border border-white/10 bg-[#0C0C0C] rounded-lg p-6" data-testid="credits-box">
              <div className="mono text-white/60 mb-4">CREDITS · CAST & CREW</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {c.credits.map((cr, i) => (
                  <Link key={i} to={`/app/network?q=${encodeURIComponent(cr.name || "")}`} data-testid={`credit-${i}`} className="group">
                    <div className="font-sans font-bold group-hover:text-kinetic transition-colors">{cr.name} <span className="text-white/30">↗</span></div>
                    <div className="mono text-white/40">{(cr.role || "").toUpperCase()} {cr.type ? `· ${cr.type}` : ""}</div>
                  </Link>
                ))}
              </div>
              <div className="mono text-white/30 mt-3">TAP A NAME TO FIND THEM ON THE NETWORKING CIRCUIT →</div>
            </div>
          )}

          <div className="border border-white/10 bg-[#0C0C0C] rounded-lg p-6" data-testid="reviews-section">
            <div className="mono text-white/60 mb-3 flex items-center gap-2"><MessageSquare className="w-3.5 h-3.5 text-kinetic" /> REACTIONS & REVIEWS</div>
            <Textarea data-testid="review-input" value={reviewText} onChange={(e) => setReviewText(e.target.value)}
              placeholder="Share your reaction (max 160 words)…" className="bg-black border-white/15 rounded-sm min-h-20" />
            <div className="flex items-center justify-between mt-2">
              <span className={`mono ${wordCount > 160 ? "text-coral" : "text-white/40"}`}>{wordCount}/160 WORDS</span>
              <Button data-testid="review-submit" onClick={addReview} disabled={wordCount === 0 || wordCount > 160} className="kinetic-btn rounded-sm">Post review</Button>
            </div>
            <div className="mt-5 space-y-4">
              {reviews.filter(r => r.is_critic).length > 0 && (
                <div className="border border-kinetic/30 bg-kinetic/5 rounded-lg p-4" data-testid="critic-reviews">
                  <div className="mono text-kinetic mb-3 flex items-center gap-2"><BadgeCheck className="w-4 h-4" /> VERIFIED CRITIC REVIEWS</div>
                  <div className="space-y-3">
                    {reviews.filter(r => r.is_critic).map(r => (
                      <div key={r.id} className="flex gap-3" data-testid={`critic-review-${r.id}`}>
                        <Avatar className="w-9 h-9 rounded-sm"><AvatarImage src={r.picture} /><AvatarFallback className="bg-white/10 rounded-sm">{r.name?.[0]}</AvatarFallback></Avatar>
                        <div className="flex-1">
                          <div className="font-sans font-bold text-sm flex items-center gap-1">{r.name} <BadgeCheck className="w-3.5 h-3.5 text-kinetic" /></div>
                          <p className="text-white/80 text-sm mt-0.5 whitespace-pre-wrap">{r.body}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {reviews.length === 0 && <div className="mono text-white/30">NO REVIEWS YET · BE THE FIRST</div>}
              {reviews.filter(r => !r.is_critic).map(r => (
                <div key={r.id} className="flex gap-3" data-testid={`review-${r.id}`}>
                  <Avatar className="w-9 h-9 rounded-sm"><AvatarImage src={r.picture} /><AvatarFallback className="bg-white/10 rounded-sm">{r.name?.[0]}</AvatarFallback></Avatar>
                  <div className="flex-1">
                    <div className="font-sans font-bold text-sm">{r.name}</div>
                    <p className="text-white/75 text-sm mt-0.5 whitespace-pre-wrap">{r.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Box office */}
        <div className="space-y-6">
          <div className="border border-kinetic/30 bg-kinetic/5 rounded-lg p-6" data-testid="box-office">
            <div className="mono text-kinetic mb-3">🎟 BOX OFFICE</div>
            {c.has_access ? (
              <div className="text-white/80"><Play className="w-5 h-5 text-kinetic inline mr-2" /> You have access — enjoy the show.</div>
            ) : tiers.length === 0 && !paidScreening ? (
              <div className="mono text-white/50">FREE TO WATCH</div>
            ) : (
              <div className="space-y-2">
                {tiers.map(t => (
                  <button key={t.key} data-testid={`buy-${t.key}`} onClick={() => buy(t.key)}
                    className="w-full flex items-center justify-between border border-white/15 hover:border-kinetic bg-black rounded-sm px-4 py-3 transition-colors">
                    <div className="text-left"><div className="font-sans font-bold">{t.label}</div><div className="mono text-white/40">{t.sub}</div></div>
                    <div className="mono text-kinetic">₹{Number(c.pricing[t.key].price).toLocaleString("en-IN")}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {paidScreening && (
            <div className="border border-white/10 bg-[#0C0C0C] rounded-lg p-6">
              <div className="mono text-white/60 mb-3">REQUEST A PAID SCREENING</div>
              <Dialog>
                <DialogTrigger asChild><Button data-testid="screening-open" variant="outline" className="w-full rounded-sm border-white/20 bg-transparent text-white hover:bg-white/10">Request screening</Button></DialogTrigger>
                <DialogContent className="bg-[#0C0C0C] border-white/10 text-white rounded-sm max-w-lg">
                  <DialogHeader><DialogTitle className="font-sans">Request a screening</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    {[["name", "Your name"], ["email", "Email"], ["preferred_date", "Preferred date"]].map(([k, l]) => (
                      <div key={k}><Label className="mono text-white/60">{l}</Label>
                        <Input data-testid={`screening-${k}`} value={screen[k]} onChange={(e) => setScreen(s => ({ ...s, [k]: e.target.value }))} className="bg-black border-white/15 rounded-sm" /></div>
                    ))}
                    <div><Label className="mono text-white/60">Message</Label>
                      <Textarea data-testid="screening-message" value={screen.message} onChange={(e) => setScreen(s => ({ ...s, message: e.target.value }))} className="bg-black border-white/15 rounded-sm" /></div>
                  </div>
                  <DialogFooter><Button data-testid="screening-submit" onClick={requestScreening} className="kinetic-btn rounded-sm">Send request →</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}

          {c.streamer && (
            <div className="border border-white/10 bg-[#0C0C0C] rounded-lg p-6">
              <div className="mono text-white/60 mb-3">STREAMER</div>
              <div className="flex items-center gap-3">
                <Avatar className="w-11 h-11 rounded-sm"><AvatarImage src={posterUrl(c.streamer.picture)} /><AvatarFallback className="bg-white/10 rounded-sm">{c.streamer.name?.[0]}</AvatarFallback></Avatar>
                <div><div className="font-sans font-bold">{c.streamer.name}</div><div className="mono text-white/40">{c.channel}</div></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
